import qs from "qs";
import { emptyApi } from "./emptyApi";

export type DailyVolume = {
  date: string;
  messages: number;
};

// ── CS Overview Dashboard ─────────────────────────────────────────────────────

export type CsChatSummary = {
  totalChats: number;
  avgResponseTimeHr: number;
  avgResponseTimeStr: string;
  totalRepliedUnder1hr: number;
  repliedUnder1hrPct: number;
};

export type CsTicketsSummary = {
  resolutionRate: number;
  overduePercent: number;
  outstandingTotal: number;
  ticketsDue: number;
  ticketsResolved: number;
};

export type CsNpsSummary = {
  totalFeedbacks: number;
  avgFilingRating: number;
  avgTicketRating: number;
  bestFilingCategory: string | null;
  worstFilingCategory: string | null;
  bestTicketCategory: string | null;
  worstTicketCategory: string | null;
};

export type CsSummary = {
  chat: CsChatSummary;
  tickets: CsTicketsSummary;
  nps: CsNpsSummary;
};

export type CsAgent = {
  name: string;
  initials: string;
  customers: number;

  chats: number;
  responseTimeHr: number;
  responseTimeStr: string;
  chatsPerCustomer: number;
  repliedUnder1hr: number;
  under1hrPct: number;

  tickets: number;
  resolutionRate: number | null;
  outstandingInvoices: number;
  etdSlipped: number;
  ticketsResolved: number;
  ticketsUnresolved: number;

  filingRating: number;
  ticketRating: number;
  totalFeedbacks: number;
  bestFor: string | null;
  worstFor: string | null;
  npsScore: number;
};

export type CsDashboardResponse = {
  summary: CsSummary;
  total_agents: number;
  daily_volume: DailyVolume[];
};

// sort_order values the BE accepts
export type CsSortOrder = "ASC" | "DSC";

// sort_key values per section
export type CsChatSortKey = "RESPONSE_TIME";
export type CsTicketSortKey = "RESOLUTION_RATE";
export type CsNpsSortKey = "NPS_SCORE";

type CsDashboardQueryParams = {
  start_date?: string;
  end_date?: string;
};

type CsChatQueryParams = {
  start_date?: string;
  end_date?: string;
  sort_key?: CsChatSortKey;
  sort_order?: CsSortOrder;
  page_num?: number;
};

type CsTicketQueryParams = {
  start_date?: string;
  end_date?: string;
  sort_key?: CsTicketSortKey;
  sort_order?: CsSortOrder;
  page_num?: number;
};

type CsNpsQueryParams = {
  start_date?: string;
  end_date?: string;
  sort_key?: CsNpsSortKey;
  sort_order?: CsSortOrder;
  page_num?: number;
};

export type CsSectionResponse = {
  agents: CsAgent[];
  total_count: number;
  total_pages: number;
  page_size: number;
};

// ─────────────────────────────────────────────────────────────────────────────

const supportDashboardApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getCsDashboard: build.query<CsDashboardResponse, CsDashboardQueryParams>({
      query: (params) => ({
        url: `/api/inkle/serviceteams/support-dashboard/${qs.stringify(params, {
          skipNulls: true,
          addQueryPrefix: true,
        })}`,
      }),
    }),

    getCsChatSection: build.query<CsSectionResponse, CsChatQueryParams>({
      query: (params) => ({
        url: `/api/inkle/serviceteams/support-dashboard/chat/${qs.stringify(
          params,
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
    }),

    getCsTicketsSection: build.query<CsSectionResponse, CsTicketQueryParams>({
      query: (params) => ({
        url: `/api/inkle/serviceteams/support-dashboard/tickets/${qs.stringify(
          params,
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
    }),

    getCsNpsSection: build.query<CsSectionResponse, CsNpsQueryParams>({
      query: (params) => ({
        url: `/api/inkle/serviceteams/support-dashboard/nps/${qs.stringify(
          params,
          { skipNulls: true, addQueryPrefix: true }
        )}`,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCsDashboardQuery,
  useGetCsChatSectionQuery,
  useGetCsTicketsSectionQuery,
  useGetCsNpsSectionQuery,
} = supportDashboardApi;
